import React, { useContext, useEffect, useRef, useState } from 'react'
import { TextField } from '@material-ui/core'
import { Controller, useWatch } from 'react-hook-form'
import { context, setCarColor, setCarModel, setCarRegistration } from '../../store'
import { fields, settings } from '../../store/constants'
import { CAR_LOOKUP, carLine, lookupResult, makeModel, plate, regProblem, regStep, regTracker, sizeHint, spaced } from '../../lib/vehicle'
import { bookingFlowVariant } from '../../lib/booking-flow'
import { AnalyticsService } from '../../services/analytics'

// The Registration box (docs/VEHICLE_LOOKUP.md). One box, REQUIRED on both
// pay paths since 20 Oct 2026 (Robin: "pls make the registration plate entry
// mandatory"): step-book blocks paying until it holds a UK plate FORMAT
// (lib/vehicle.js regProblem), never until a lookup answers. A wrong format
// says so under the box when the customer leaves it
// (registration_invalid_format). A plate it can read is
// looked up (GET /api/v2/vehicles, debounced) and shown as "Black BMW X5,
// 2021"; the order gets the plate, make and model, and colour through the
// store, whichever way the customer pays. No answer (lookup off, unknown
// plate, offline) shows nothing; the plate still rides the order.
// `classes` are Book & pay's own, so the row looks like its neighbours.
// payPath: 'wallet' | 'card', the path the page is on (for the events).
//
// Measured since 19 Oct 2026 (ANALYTICS.md "Registration box"):
// registration_entered once per distinct plate the customer typed and that
// was still in the box when its lookup answered (a plate the draft put back
// is not typed, so not counted); registration_size_hint_shown /
// _followed for the "looks like a Large car" line.
//
// Form v3 (20 Oct 2026): `onAnswer(car | null)` after every lookup, and the
// box shows no line of its own: Book & pay sets the size from the car's
// suggested_size and says what it did (step-book).
export const RegLookup = ({ form, classes, onFocus, payPath, onAnswer }) => {
  const { dispatch } = useContext(context)
  const reg = plate(useWatch({ control: form.control, name: fields.carRegistration }))
  const size = useWatch({ control: form.control, name: fields.carSize })
  const [car, setCar] = useState(null)
  const prevReg = useRef()
  // The plate the shown car belongs to, so the hint events never credit a
  // car to the plate typed after it.
  const carReg = useRef('')
  const typed = useRef(false)
  const tracker = useRef(null)
  if (!tracker.current) tracker.current = regTracker()
  const pathRef = useRef(payPath)
  pathRef.current = payPath
  const answerRef = useRef()
  answerRef.current = onAnswer
  const ctx = () => ({ form_variant: bookingFlowVariant(), path: pathRef.current || null })

  // A plate the Book & pay draft puts back (reload, return from Stripe) is
  // never cleared: its kept car is shown again, or quietly looked up when
  // none was kept (lib/vehicle.js regStep).
  useEffect(() => {
    const step = regStep(prevReg.current, reg, form.getValues(CAR_LOOKUP))
    prevReg.current = reg
    if (!step) return
    let live = true
    const show = (found, keep) => {
      if (!live) return
      carReg.current = found ? reg : ''
      setCar(found)
      dispatch(setCarModel(makeModel(found)))
      dispatch(setCarColor(found?.colour || ''))
      if (keep) form.setValue(CAR_LOOKUP, found ? { ...found, reg } : null)
    }
    dispatch(setCarRegistration(spaced(reg)))
    if (step.car) show(step.car)
    else if (step.clear) show(null, true)
    const timer = step.lookUp && setTimeout(() => {
      fetch(`${settings.host}/api/v2/vehicles?reg=${reg}`)
        .then(r => (r.ok ? r.json().then(found => [r.status, found]) : [r.status, null]))
        .catch(() => [0, null])
        .then(([status, found]) => {
          if (!live) return
          if (typed.current) {
            const props = tracker.current.entered(reg, lookupResult(status), found, form.getValues(fields.carSize), ctx())
            if (props) AnalyticsService.trackRegistration('entered', props)
          }
          if (found) show(found, true)
          if (answerRef.current) answerRef.current(found)
        })
    }, 600)
    return () => { live = false; clearTimeout(timer) }
  }, [reg, dispatch])

  const line = !onAnswer && carLine(car)
  const hint = !onAnswer && sizeHint(car, size)

  useEffect(() => {
    const event = tracker.current.hint(carReg.current, !!hint, size)
    if (event) AnalyticsService.trackRegistration(`size_hint_${event}`, ctx())
  }, [hint, size, car])

  return (
    <div className={classes.detailRow}>
      <label className={classes.fieldLabel} htmlFor={`book-${fields.carRegistration}`}>
        Registration
      </label>
      <div className={`${classes.field} ${classes.compactField}`}>
        <Controller
          name={fields.carRegistration}
          control={form.control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              onChange={e => { typed.current = true; field.onChange(e) }}
              id={`book-${fields.carRegistration}`}
              fullWidth
              placeholder="e.g. AB12 CDE"
              error={!!error}
              helperText={error?.message || null}
              InputProps={{
                disableUnderline: true,
                onFocus,
                // A typed plate in no UK format says so now (as the mobile
                // does); an empty box waits for the pay press.
                onBlur: () => {
                  field.onBlur()
                  const bad = field.value && regProblem(field.value)
                  if (bad) {
                    setTimeout(() => form.setError(fields.carRegistration, { type: 'manual', message: bad.message }), 0)
                    AnalyticsService.trackRegistration('invalid_format', { reason: bad.reason, ...ctx() })
                  } else form.clearErrors(fields.carRegistration)
                }
              }}
              inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', spellCheck: false }}
            />
          )}
        />
      </div>
      {line && <p className={classes.addressHint} data-testid="reg-car">{line}{hint && `. ${hint}`}</p>}
    </div>
  )
}
